type BatchHandler<T> = (batch: T[]) => Promise<void>;

interface StreamJsonArrayOptions<T> {
  batchSize: number;
  mapItem?: (item: unknown) => T;
}

export async function streamJsonArray<T>(
  response: Response,
  onBatch: BatchHandler<T>,
  options: StreamJsonArrayOptions<T>,
): Promise<number> {
  if (!response.body) {
    const items = (await response.json()) as unknown[];
    const mapped = options.mapItem
      ? items.map(options.mapItem)
      : (items as T[]);
    for (let i = 0; i < mapped.length; i += options.batchSize) {
      const chunk = mapped.slice(i, i + options.batchSize);
      await onBatch(chunk);
    }
    return mapped.length;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const batch: T[] = [];
  let total = 0;
  let objectBuffer = '';
  let depth = 0;
  let inString = false;
  let isEscaped = false;

  const pushItem = async (rawObject: string) => {
    const parsed = JSON.parse(rawObject) as unknown;
    batch.push(options.mapItem ? options.mapItem(parsed) : (parsed as T));
    total += 1;

    if (batch.length >= options.batchSize) {
      await flushBatch(batch, onBatch);
    }
  };

  const consume = async (chunk: string) => {
    let sliceStart: number | null = depth > 0 ? 0 : null;

    for (let index = 0; index < chunk.length; index += 1) {
      const char = chunk[index];

      if (isEscaped) {
        isEscaped = false;
        continue;
      }

      if (char === '\\' && inString) {
        isEscaped = true;
        continue;
      }

      if (char === '"') {
        inString = !inString;
        continue;
      }

      if (inString) {
        continue;
      }

      if (char === '{') {
        if (depth === 0) {
          sliceStart = index;
        }
        depth += 1;
        continue;
      }

      if (char === '}') {
        depth -= 1;
        if (depth === 0) {
          const start = sliceStart ?? 0;
          await pushItem(objectBuffer + chunk.slice(start, index + 1));
          objectBuffer = '';
          sliceStart = null;
        }
      }
    }

    if (depth > 0 && sliceStart !== null) {
      objectBuffer += chunk.slice(sliceStart);
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    await consume(decoder.decode(value, { stream: true }));
  }

  const finalChunk = decoder.decode();
  if (finalChunk) {
    await consume(finalChunk);
  }

  await flushBatch(batch, onBatch);
  return total;
}

async function flushBatch<T>(batch: T[], onBatch: BatchHandler<T>) {
  if (batch.length === 0) {
    return;
  }

  // Copy first, clear after — if onBatch throws the items remain in batch and are not lost.
  const items = [...batch];
  await onBatch(items);
  batch.splice(0, batch.length);
}
